import { useEffect, useRef, useState } from "react";

type T = (key: string, vars?: Record<string, string | number>) => string;

// Counter animation: 40 frames, 60ms apart, cubic ease-out (same as the approved design).
const FRAMES = 40;
const FRAME_MS = 60;

function prefersReducedMotion() {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Counts the bento numbers up once the grid scrolls into view (Amazon +$48, eBay +$29,
 * "24 left" down to 9) and flags the grid live so the $49.97 strike / $0.01 pop play then.
 * Reduced motion shows the final values straight away.
 */
function useBentoCounters() {
  const ref = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState(() => (prefersReducedMotion() ? FRAMES : 0));
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setFrame(FRAMES);
      return;
    }
    let timer: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      setLive(true);
      timer = setInterval(() => {
        setFrame((f) => {
          if (f + 1 >= FRAMES) clearInterval(timer);
          return Math.min(FRAMES, f + 1);
        });
      }, FRAME_MS);
    };
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      start();
      return () => clearInterval(timer);
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        start();
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearInterval(timer);
    };
  }, []);

  const k = Math.min(1, frame / FRAMES);
  const ease = 1 - Math.pow(1 - k, 3);
  return {
    ref,
    live,
    amazon: Math.round(48 * ease),
    ebay: Math.round(29 * ease),
    left: Math.max(9, 24 - Math.round(15 * ease)),
  };
}

export default function ResellerBento({ t }: { t: T }) {
  const { ref, live, amazon, ebay, left } = useBentoCounters();

  return (
    <section className="rl-section" aria-labelledby="rl-bento-title">
      <h2 id="rl-bento-title" className="sr-only">{t("resellerLanding.bentoLabel")}</h2>
      <div ref={ref} className={`rl-bento${live ? " is-live" : ""}`}>
        <div className="rl-tile rl-resale rl-span2 rl-row2 rl-reveal">
          <div className="rl-resale-copy">
            <span className="rl-badge">Pro</span>
            <h3>{t("resellerLanding.resaleTitle")}</h3>
            <p>{t("resellerLanding.resaleText")}</p>
            <div className="rl-chips">
              <span>{t("resellerLanding.resaleAmazon", { amount: amazon })}</span>
              <span>{t("resellerLanding.resaleEbay", { amount: ebay })}</span>
            </div>
            <span className="rl-chip-note">{t("resellerLanding.resaleExample")}</span>
          </div>
          <img
            className="rl-resale-shot"
            src="/landing/app/light-resale.webp"
            alt={t("resellerLanding.resaleAlt")}
            width={600}
            height={1224}
            loading="lazy"
          />
        </div>

        <div className="rl-tile rl-tile-text rl-clock rl-reveal">
          <div className="rl-big">
            {t("resellerLanding.alertsBig")}
          </div>
          <div>
            <p>{t("resellerLanding.alertsText")}</p>
          </div>
        </div>

        <div className="rl-tile rl-tile-text rl-stock rl-reveal">
          <div className="rl-big">{t("resellerLanding.stockLeft", { count: left })}</div>
          <div>
            <h3>{t("resellerLanding.stockTitle")}</h3>
            <p>{t("resellerLanding.stockText")}</p>
          </div>
        </div>

        <div className="rl-tile rl-tile-text rl-penny rl-reveal">
          <div>
            <div className="rl-was">
              <span className="sr-only">{t("resellerLanding.pennyWas")} </span>$49.97
            </div>
            <div>
              <span className="rl-big rl-cent">$0.01</span>
            </div>
          </div>
          <div>
            <h3>{t("resellerLanding.pennyTitle")}</h3>
            <p>{t("resellerLanding.pennyText")}</p>
          </div>
        </div>

        <div className="rl-tile rl-feed rl-reveal">
          <h3>{t("resellerLanding.feedTitle")}</h3>
          <p>{t("resellerLanding.feedText")}</p>
          <img
            className="rl-feed-shot"
            src="/landing/app/light-feed.webp"
            alt={t("resellerLanding.feedAlt")}
            width={480}
            height={980}
            loading="lazy"
          />
        </div>

        <div className="rl-tile rl-photo rl-span2 rl-reveal">
          <img
            src="/landing/community/penny-milwaukee-cart.webp"
            alt={t("resellerLanding.cartAlt")}
            width={900}
            height={1200}
            loading="lazy"
          />
          <span>{t("resellerLanding.cartCaption")}</span>
        </div>

        <div className="rl-tile rl-discord rl-span2 rl-reveal">
          <img src="/landing/illustrations/n6-community.webp" alt="" width={805} height={805} loading="lazy" />
          <div>
            <h3>{t("resellerLanding.discordTitle")}</h3>
            <p>{t("resellerLanding.discordText")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
